import { useEffect, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useTwilioDevice } from '@/hooks/useTwilioDevice'
import { useTelnyxPhone } from '@/hooks/useTelnyxPhone'
import { useIncomingCall } from '@/hooks/useIncomingCall'
import { useDashboardLifecycle } from '@/hooks/useDashboardLifecycle'
import { useClickToCallListener } from '@/hooks/useClickToCallChannel'
import { useCallStore } from '@/stores/useCallStore'
import { useTabStateStore } from '@/stores/useTabStateStore'
import { getLocaleFromPath, defaultLocale } from '@/lib/locales'
import { initDesktopShell } from '@/lib/desktopShell'
import { BandeauTelephone } from '@/components/BandeauTelephone'

/**
 * Wrapper React qui monte les téléphones de l'agent (Twilio, et Telnyx si
 * l'entreprise a un numéro Telnyx) + side effects dashboard à un seul endroit.
 *
 * À placer au-dessus du dashboard (et seulement du dashboard) — pas globalement,
 * car on ne veut pas init Twilio sur les pages publiques ou pendant onboarding.
 *
 * Pattern :
 *   <AuthGuard>
 *     <VoiceProvider>
 *       <DashboardLayout />
 *     </VoiceProvider>
 *   </AuthGuard>
 */
export function VoiceProvider({ children }: { children: ReactNode }) {
  useTwilioDevice()
  useTelnyxPhone()
  useIncomingCall()
  useDashboardLifecycle()
  useClickToCallReceiver()
  useEffect(() => {
    initDesktopShell()
  }, [])
  return (
    <>
      {children}
      <BandeauTelephone />
    </>
  )
}

/**
 * Côté dashboard : répond aux pings click-to-call émis par les nouveaux
 * onglets `/call?number=...`. Si pas d'appel en cours, navigate vers le
 * PhoneTab avec les query params auto-call.
 *
 * Désactivé quand le tab est inactif (un autre tab a pris le contrôle Device).
 */
function useClickToCallReceiver() {
  const navigate = useNavigate()
  const location = useLocation()

  useClickToCallListener({
    isBusy: () => {
      // Tab inactif → ne répond pas ; l'émetteur tombe en fallback après
      // 300 ms (navigate normal, qui réactive ce tab après login). Répondre
      // « busy » affichait à tort « un appel est déjà en cours ».
      if (!useTabStateStore.getState().isActive) return 'inactive'
      return useCallStore.getState().currentCall !== null
    },
    onDial: (number, auto) => {
      // Pas de toast : la GlobalCallModal/InCallWidget vont s'afficher avec
      // le numéro dès que makeOutboundCall est appelé. Doublon visuel évité.
      const locale = getLocaleFromPath(location.pathname) || defaultLocale
      const params = new URLSearchParams()
      params.set('view', 'phone')
      params.set('call', number)
      if (auto) params.set('auto', '1')
      navigate(`/${locale}/dashboard?${params.toString()}`)

      // Best-effort : tenter de remettre cet onglet au premier plan.
      // window.focus() est souvent ignoré par les navigateurs sans user-gesture
      // direct, mais ça coûte rien d'essayer — certains environnements (Electron,
      // PWA installée, certaines configs Chrome/Edge) honorent l'appel.
      try {
        window.focus()
      } catch {
        // ignoré silencieusement
      }
    },
  })
}
